import React, { useState } from 'react';
import {
  PresetDiscoveryBadge,
  PresetLayoutAction,
} from '@src/components/ActionToolbar/ApplyPreset';
import usePersonalPresetsEnabled from '@src/hooks/usePersonalPresetsEnabled';
import { Popover, Space } from 'antd';
import useContainerRef from '@src/hooks/utils/useContainerRef';
import { LayoutBtn, StyledDivider, StyledResizeLabel, StyledResizeWrapper } from './styled';
import { useAtom, useAtomValue } from 'jotai';
import { diagnosticsModeAtom, layoutAtom } from '../atoms';

import L1a from '@icons/layout/l1a.svg';
import L2a from '@icons/layout/l2a.svg';
import L2b from '@icons/layout/l2b.svg';
import L3a from '@icons/layout/l3a.svg';
import L3b from '@icons/layout/l3b.svg';
import L4a from '@icons/layout/l4a.svg';
import L4b from '@icons/layout/l4b.svg';
import L4c from '@icons/layout/l4c.svg';
import { noop } from 'lodash';
import Resize from '@src/components/dimension-panel-hz/header/Resize';
import { DownOutlined } from '@ant-design/icons';
import apptracker from '@src/utils/sensor/apptracker';
import { LAYOUT_CHANGE } from '@src/utils/sensor/track';

const options = {
  '1s': { icon: L1a },
  '2s': { icon: L2a },
  '2l': { icon: L2b },
  '3s': { icon: L3a },
  '3l': { icon: L3b },
  '4s': { icon: L4a },
  '4m': { icon: L4b },
  '4l': { icon: L4c },
};

export const getChartStyle = (size, diagMode) => {
  return diagMode
    ? {
        gridTemplateColumns: '1fr',
      }
    : {
        gridTemplateColumns: `repeat(auto-fill,minmax(${size.length}, 1fr))`,
      };
};

const getOption: any = (value, fn) => {
  const Icon = options[value].icon;
  const { onClick, highlight } = fn(value);
  return {
    // size: "small",
    type: 'text',
    icon: <Icon />,
    onClick,
    $highlight: highlight,
  };
};

const Content = ({ onChange, selected }) => {
  const fnWrap = (value) => ({
    onClick: () => onChange(value),
    highlight: value === selected,
  });

  return (
    <Space direction="vertical">
      <LayoutBtn {...getOption('1s', fnWrap)} />
      <StyledDivider />
      <Space>
        <LayoutBtn {...getOption('2s', fnWrap)} />
        <LayoutBtn {...getOption('2l', fnWrap)} />
      </Space>
      <StyledDivider />
      <Space>
        <LayoutBtn {...getOption('3s', fnWrap)} />
        <LayoutBtn {...getOption('3l', fnWrap)} />
      </Space>
      <StyledDivider />
      <Space>
        <LayoutBtn {...getOption('4s', fnWrap)} />
        <LayoutBtn {...getOption('4m', fnWrap)} />
        <LayoutBtn {...getOption('4l', fnWrap)} />
      </Space>
      <StyledDivider />
      <Space>
        <Resize />
      </Space>
    </Space>
  );
};

function PanelResize() {
  const [open, setOpen] = useState(false);
  const presetsEnabled = usePersonalPresetsEnabled();
  const [panelSize, setPanelSize] = useAtom(layoutAtom);
  const { containerRef, getContainer } = useContainerRef();

  const diagMode = useAtomValue(diagnosticsModeAtom);

  const handlePanelSizeChange = (value) => {
    setPanelSize(value);
    apptracker(LAYOUT_CHANGE, { value });
  };

  return (
    <PresetDiscoveryBadge>
      <StyledResizeWrapper ref={containerRef}>
        <StyledResizeLabel>Layout</StyledResizeLabel>
        <Popover
          content={
            <Space direction="vertical">
              {presetsEnabled && (
                <>
                  <PresetLayoutAction onOpen={() => setOpen(false)} />
                  <StyledDivider />
                  <div className="ant-popover-title">Layout Options</div>
                </>
              )}
              <Content onChange={handlePanelSizeChange} selected={panelSize} />
            </Space>
          }
          open={open}
          onOpenChange={setOpen}
          title={presetsEnabled ? 'Metric, Dimension Layout' : 'Layout Options'}
          getPopupContainer={getContainer}
          placement="bottomLeft"
          trigger={diagMode ? [] : ['hover', 'click']}
        >
          <LayoutBtn
            aria-label="Layout options"
            {...getOption(panelSize, () => ({ onClick: noop, highlight: false }))}
            style={{ padding: '0px 11px', width: 'auto' }}
            disabled={diagMode}
          >
            <DownOutlined style={{ fontSize: 12, color: '#c1c1c1', padding: 3 }} />
          </LayoutBtn>
        </Popover>
      </StyledResizeWrapper>
    </PresetDiscoveryBadge>
  );
}

export default PanelResize;
